import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Copy, Check, CheckCircle, XCircle,
  Building2, RefreshCw, Edit, Trash2, QrCode,
  ExternalLink, CheckCircle2, Circle, Bot, Eye, EyeOff,
} from 'lucide-react';
import {
  getChannelLineStatus, getLineMenus, getLineAliases,
  getLineMenuDetail, getChannelBotInfo, validateChannel,
  updateChannel, deleteChannel, getUserMenu, clearUserMenu,
  getChannelVisitors, deleteLineMenus,
} from '../services/api';
import { useChannel } from '../context/ChannelContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div>
      <p className="text-xs text-[#52525B] mb-1">{label}</p>
      <div className="flex items-center gap-2">
        <p className="flex-1 text-xs font-mono bg-[#09090B] border border-[#27272A] rounded-md px-3 py-2 text-[#A1A1AA] break-all">{value}</p>
        <button onClick={copy} className="flex-shrink-0 p-2 rounded-md border border-[#27272A] hover:bg-[#27272A] transition-colors">
          {copied ? <Check className="w-3.5 h-3.5 text-[#06C755]" /> : <Copy className="w-3.5 h-3.5 text-[#52525B]" />}
        </button>
      </div>
    </div>
  );
}

export function ChannelDetail() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const { channels, refreshChannels, selectedChannelId } = useChannel();
  const { refreshWorkspaces, isWorkspaceAdmin } = useWorkspace();
  const { isSuperAdmin } = useAuth();
  // Editing/deleting the channel and deleting LINE menus are admin-only on the server
  const canManage = isWorkspaceAdmin || isSuperAdmin;

  const channel = channels.find(c => c.id === channelId);

  // LINE status state
  const [lineData, setLineData] = useState(null);
  const [lineLoading, setLineLoading] = useState(false);

  // Bot info
  const [botInfo, setBotInfo] = useState(null);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editToken, setEditToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Validating
  const [validating, setValidating] = useState(false);

  // User override
  const [userInput, setUserInput] = useState('');
  const [userResult, setUserResult] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const [visitors, setVisitors] = useState(null);
  const [visitorsLoading, setVisitorsLoading] = useState(false);

  // LINE menu selection + delete
  const [selectedMenuIds, setSelectedMenuIds] = useState(new Set());
  const [lineDeleteConfirm, setLineDeleteConfirm] = useState(false);
  const [lineDeleteConfirm2, setLineDeleteConfirm2] = useState(false);
  const [lineDeleting, setLineDeleting] = useState(false);

  // QR
  const [qrOpen, setQrOpen] = useState(false);

  const loadLineData = useCallback(async () => {
    if (!channelId) return;
    setLineLoading(true);
    try {
      const [status, menus, aliases] = await Promise.all([
        getChannelLineStatus(channelId),
        getLineMenus(channelId),
        getLineAliases(channelId),
      ]);
      const aliasMap = {};
      (aliases?.aliases || []).forEach(a => { aliasMap[a.richMenuId] = a.richMenuAliasId; });
      let activeMenuDetail = null;
      if (status?.line?.defaultRichMenuId) {
        try { activeMenuDetail = await getLineMenuDetail(channelId, status.line.defaultRichMenuId); } catch {}
      }
      setLineData({ status, menus: menus?.richmenus || [], aliasMap, activeMenuDetail });
    } catch {
      toast.error('Failed to load LINE data');
    } finally {
      setLineLoading(false);
    }
  }, [channelId]);

  useEffect(() => {
    if (!channel) return;
    loadLineData();
    getChannelBotInfo(channelId).then(setBotInfo).catch(() => {});
  }, [channel, channelId, loadLineData]);

  useEffect(() => {
    if (!channel && channels.length > 0) navigate('/channels');
  }, [channel, channels, navigate]);

  if (!channel) return null;

  const webhookUrl = `${BACKEND_URL}/api/webhook/${channelId}`;

  const handleValidate = async () => {
    setValidating(true);
    try {
      const r = await validateChannel(channelId);
      await refreshChannels();
      if (r.isValid) {
        toast.success('Token is valid');
        getChannelBotInfo(channelId).then(setBotInfo).catch(() => {});
      } else {
        toast.error('Token validation failed');
      }
    } catch { toast.error('Failed to validate'); }
    finally { setValidating(false); }
  };

  const handleSave = async () => {
    if (!editName.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const data = { name: editName, description: editDesc };
      if (editToken.trim()) data.channelAccessToken = editToken;
      await updateChannel(channelId, data);
      await refreshChannels();
      toast.success('Channel updated');
      setEditOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteChannel(channelId);
      await Promise.all([refreshChannels(), refreshWorkspaces()]);
      toast.success('Channel deleted');
      navigate('/channels');
    } catch { toast.error('Failed to delete channel'); }
    finally { setDeleting(false); setDeleteConfirmOpen(false); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">

      {/* Back */}
      <button onClick={() => navigate('/channels')} className="flex items-center gap-1.5 text-sm text-[#71717A] hover:text-white mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Channels
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-[#06C755]/10 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-6 h-6 text-[#06C755]" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold">{channel.name}</h1>
              <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${channel.isValid ? 'bg-[#06C755]/10 text-[#06C755]' : 'bg-yellow-500/10 text-yellow-500'}`}>
                {channel.isValid ? <><CheckCircle className="w-3 h-3" />Valid</> : <><XCircle className="w-3 h-3" />Invalid</>}
              </span>
              {selectedChannelId === channelId && (
                <span className="px-2 py-0.5 rounded bg-[#00E05E]/10 text-[#00E05E] text-xs">Selected</span>
              )}
            </div>
            {botInfo && (
              <div className="flex items-center gap-2 mt-1">
                <Bot className="w-3.5 h-3.5 text-[#52525B]" />
                <span className="text-sm text-[#A1A1AA]">{botInfo.displayName}</span>
                {botInfo.basicId && <span className="text-xs text-[#52525B] font-mono">{botInfo.basicId}</span>}
              </div>
            )}
            {channel.description && <p className="text-sm text-[#71717A] mt-0.5">{channel.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {botInfo?.basicId && (
            <Button variant="outline" size="sm" onClick={() => setQrOpen(true)} className="border-[#27272A]">
              <QrCode className="w-4 h-4" />
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleValidate} disabled={validating} className="border-[#27272A]">
            <RefreshCw className={`w-4 h-4 ${validating ? 'animate-spin' : ''}`} />
          </Button>
          {canManage && (
            <Button variant="outline" size="sm" onClick={() => { setEditName(channel.name); setEditDesc(channel.description || ''); setEditToken(''); setEditOpen(true); }} className="border-[#27272A]">
              <Edit className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Channel info */}
      <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4 space-y-4 mb-6">
        <CopyField label="Channel ID" value={channelId} />
        <CopyField label="Webhook URL" value={webhookUrl} />
      </div>

      {/* LINE Status */}
      <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold">LINE Status</p>
          <Button variant="ghost" size="sm" onClick={loadLineData} disabled={lineLoading} className="h-7 text-xs text-[#52525B]">
            <RefreshCw className={`w-3 h-3 mr-1.5 ${lineLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {lineLoading && !lineData ? (
          <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-8 rounded bg-[#27272A] animate-pulse" />)}</div>
        ) : lineData ? (
          <div className="space-y-5">

            {/* Default menu */}
            <div>
              <p className="text-xs font-semibold text-[#52525B] uppercase tracking-wider mb-2">Default menu (shown to all users without a personal menu)</p>
              {lineData.status.line.hasDefault ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-3 bg-[#06C755]/5 border border-[#06C755]/20 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-[#06C755] flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-mono text-[#FAFAFA] truncate">{lineData.status.line.defaultRichMenuId}</p>
                      <div className="flex flex-wrap gap-x-3 mt-0.5">
                        {lineData.status.db.menuName && <p className="text-xs text-[#71717A]">{lineData.status.db.menuName}</p>}
                        {lineData.activeMenuDetail && (
                          <>
                            {lineData.activeMenuDetail.chatBarText && <p className="text-xs text-[#52525B]">bar: "{lineData.activeMenuDetail.chatBarText}"</p>}
                            <p className="text-xs text-[#52525B]">areas: {lineData.activeMenuDetail.areas}</p>
                          </>
                        )}
                      </div>
                    </div>
                    {!lineData.status.inSync && <span className="text-xs text-yellow-400 flex-shrink-0">out of sync with DB</span>}
                  </div>
                  {lineData.activeMenuDetail?.selected === false && (
                    <div className="p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-lg text-xs text-yellow-400">
                      <span className="font-medium">⚠ Menu is collapsed by default</span>
                      <span className="text-yellow-500/70 ml-1">— <code>selected: false</code> means users see only the chat bar and must tap to expand.</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-[#27272A] rounded-lg">
                  <Circle className="w-4 h-4 text-[#52525B]" />
                  <p className="text-sm text-[#71717A]">No default menu set on LINE</p>
                </div>
              )}
            </div>

            {/* Menus list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-[#52525B] uppercase tracking-wider">Menus on LINE ({lineData.menus.length})</p>
                {canManage && selectedMenuIds.size > 0 && (
                  <Button size="sm" variant="outline"
                    onClick={() => setLineDeleteConfirm(true)}
                    className="h-7 text-xs border-red-500/30 text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-3 h-3 mr-1.5" />
                    Delete {selectedMenuIds.size} from LINE
                  </Button>
                )}
              </div>
              {lineData.menus.length === 0 ? (
                <p className="text-sm text-[#52525B]">No rich menus on LINE for this channel</p>
              ) : (
                <div className="space-y-1.5">
                  {lineData.menus.map(m => {
                    const alias = lineData.aliasMap[m.richMenuId];
                    const isActive = m.richMenuId === lineData.status.line.defaultRichMenuId;
                    const isSelected = selectedMenuIds.has(m.richMenuId);
                    return (
                      <div
                        key={m.richMenuId}
                        onClick={() => {
                          if (isActive || !canManage) return;
                          setSelectedMenuIds(prev => {
                            const next = new Set(prev);
                            next.has(m.richMenuId) ? next.delete(m.richMenuId) : next.add(m.richMenuId);
                            return next;
                          });
                        }}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors
                          ${isActive ? 'border-[#06C755]/30 bg-[#06C755]/5 cursor-default'
                            : !canManage ? 'border-[#27272A] bg-[#09090B] cursor-default'
                            : isSelected ? 'border-red-500/30 bg-red-500/5 cursor-pointer'
                            : 'border-[#27272A] bg-[#09090B] cursor-pointer hover:border-[#3F3F46]'}`}
                      >
                        {/* Checkbox / active indicator */}
                        {isActive ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#06C755] flex-shrink-0" />
                        ) : canManage && (
                          <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center transition-colors
                            ${isSelected ? 'bg-red-500/20 border-red-400' : 'border-[#3F3F46]'}`}>
                            {isSelected && <Check className="w-2.5 h-2.5 text-red-400" />}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-mono text-[#A1A1AA] truncate">{m.richMenuId}</p>
                          {m.name && <p className="text-xs text-[#52525B] truncate">{m.name}</p>}
                        </div>
                        {isActive && <span className="text-xs text-[#06C755] flex-shrink-0">default</span>}
                        {alias
                          ? <span className="text-xs font-mono px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20 flex-shrink-0">{alias}</span>
                          : !isActive && <span className="text-xs text-[#3F3F46] flex-shrink-0">no alias</span>
                        }
                      </div>
                    );
                  })}
                  {canManage && lineData.menus.some(m => m.richMenuId !== lineData.status.line.defaultRichMenuId) && (
                    <p className="text-[10px] text-[#3F3F46] mt-1">Click a menu to select it for deletion. Menus that are live on LINE (the default menu, or a menu an alias points to) can't be deleted.</p>
                  )}
                </div>
              )}
            </div>

            {/* User override */}
            <div>
              <p className="text-xs font-semibold text-[#52525B] uppercase tracking-wider mb-2">Check user override</p>
              <p className="text-xs text-[#52525B] mb-3">If a user sees a different menu, they may have a user-level override. Enter their LINE user ID (<span className="font-mono">U...</span>) to check, or load users captured via webhook.</p>

              {/* Webhook visitors */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs text-[#52525B]">Users captured via webhook</p>
                  <button
                    onClick={async () => {
                      setVisitorsLoading(true);
                      try {
                        const r = await getChannelVisitors(channelId);
                        setVisitors(r.visitors || []);
                      } catch { toast.error('Failed to load visitors'); }
                      finally { setVisitorsLoading(false); }
                    }}
                    disabled={visitorsLoading}
                    className="text-xs text-[#06C755] hover:underline disabled:opacity-50"
                  >
                    {visitorsLoading ? 'Loading...' : visitors === null ? 'Load' : 'Refresh'}
                  </button>
                </div>
                {visitors !== null && (
                  <div className="rounded-lg border border-[#27272A] bg-[#09090B] overflow-hidden">
                    {visitors.length === 0 ? (
                      <p className="text-xs text-[#52525B] text-center py-3">No users captured yet — send a message to the bot first</p>
                    ) : (
                      <div className="max-h-36 overflow-y-auto divide-y divide-[#27272A]">
                        {visitors.map(v => (
                          <button
                            key={v.userId}
                            onClick={() => { setUserInput(v.userId); setUserResult(null); }}
                            className={`w-full flex items-center justify-between px-3 py-2 text-xs font-mono transition-colors hover:bg-[#27272A] ${userInput === v.userId ? 'bg-[#06C755]/10 text-[#06C755]' : 'text-[#A1A1AA]'}`}
                          >
                            <span>{v.userId}</span>
                            <span className="text-[#52525B] font-sans text-[10px] ml-3 flex-shrink-0">
                              {new Date(v.lastSeen).toLocaleString()}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <input
                  value={userInput}
                  onChange={e => { setUserInput(e.target.value); setUserResult(null); }}
                  placeholder="Uxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="flex-1 h-8 px-3 text-xs font-mono bg-[#09090B] border border-[#27272A] rounded-md text-[#FAFAFA] placeholder:text-[#52525B] focus:outline-none focus:border-[#52525B]"
                />
                <Button size="sm" variant="outline" disabled={!userInput.trim() || userLoading} className="border-[#27272A] h-8 text-xs px-3"
                  onClick={async () => {
                    setUserLoading(true); setUserResult(null);
                    try {
                      const r = await getUserMenu(channelId, userInput.trim());
                      setUserResult({ type: 'check', data: r });
                    } catch (e) {
                      setUserResult({ type: 'error', message: e.response?.data?.detail || 'Failed' });
                    } finally { setUserLoading(false); }
                  }}>
                  {userLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Check'}
                </Button>
              </div>
              {userResult && (
                <div className={`mt-3 p-3 rounded-lg border text-xs ${userResult.type === 'error' ? 'border-red-500/20 bg-red-500/5 text-red-400' : userResult.data?.hasUserOverride ? 'border-yellow-500/20 bg-yellow-500/5' : 'border-[#27272A] bg-[#09090B]'}`}>
                  {userResult.type === 'error' ? userResult.message
                    : userResult.data?.hasUserOverride ? (
                      <div className="space-y-2">
                        <p className="text-yellow-400 font-medium">User has an override — channel default is bypassed</p>
                        <p className="font-mono text-[#A1A1AA]">{userResult.data.richMenuId}</p>
                        <Button size="sm" variant="outline" disabled={userLoading}
                          className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 h-7 text-xs"
                          onClick={async () => {
                            setUserLoading(true);
                            try {
                              await clearUserMenu(channelId, userInput.trim());
                              toast.success('User override cleared');
                              setUserResult({ type: 'check', data: { hasUserOverride: false } });
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Failed to clear');
                            } finally { setUserLoading(false); }
                          }}>
                          Clear override
                        </Button>
                      </div>
                    ) : <p className="text-[#71717A]">No user-level override — channel default applies</p>
                  }
                </div>
              )}
            </div>

          </div>
        ) : null}
      </div>

      {/* Danger zone */}
      {canManage && (
        <div className="bg-[#18181B] border border-red-500/20 rounded-lg p-4">
          <p className="text-sm font-semibold text-red-400 mb-1">Danger Zone</p>
          <p className="text-xs text-[#71717A] mb-4">Deleting this channel permanently removes it and all associated menus. This cannot be undone.</p>
          <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Channel
          </Button>
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Channel</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Update channel configuration</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm text-[#A1A1AA]">Channel Name</Label>
              <Input value={editName} onChange={e => setEditName(e.target.value)} className="bg-[#09090B] border-[#27272A] mt-1" />
            </div>
            <div>
              <Label className="text-sm text-[#A1A1AA]">Channel Access Token <span className="text-[#52525B]">(leave empty to keep current)</span></Label>
              <div className="relative mt-1">
                <Input type={showToken ? 'text' : 'password'} value={editToken} onChange={e => setEditToken(e.target.value)}
                  placeholder="••••••••••••••••" className="bg-[#09090B] border-[#27272A] pr-10 font-mono text-sm" />
                <button type="button" onClick={() => setShowToken(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#A1A1AA]">
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-sm text-[#A1A1AA]">Description (optional)</Label>
              <Input value={editDesc} onChange={e => setEditDesc(e.target.value)} className="bg-[#09090B] border-[#27272A] mt-1" />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setEditOpen(false)} className="border-[#27272A]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-primary">{saving ? 'Saving...' : 'Update Channel'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete step 1 */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-[#18181B] border-[#27272A] max-w-[90vw] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{channel.name}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              This will delete the channel and all menus associated with it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="bg-[#27272A] border-[#3F3F46] hover:bg-[#3F3F46]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setDeleteOpen(false); setDeleteConfirmOpen(true); }} className="bg-red-600 hover:bg-red-700">
              Yes, delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete step 2 — final confirm */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent className="bg-[#18181B] border-red-500/30 max-w-[90vw] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              This is permanent and cannot be undone. All menus for this channel will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="bg-[#27272A] border-[#3F3F46] hover:bg-[#3F3F46]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? 'Deleting...' : 'Delete permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* LINE menus delete — step 1 */}
      <AlertDialog open={lineDeleteConfirm} onOpenChange={setLineDeleteConfirm}>
        <AlertDialogContent className="bg-[#18181B] border-[#27272A] max-w-[90vw] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedMenuIds.size} menu{selectedMenuIds.size !== 1 ? 's' : ''} from LINE?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-[#A1A1AA]">
                <p>This will permanently delete the following menus <span className="text-white font-medium">directly from the LINE API</span>. These are <span className="text-white font-medium">not</span> your local menus — this removes them from LINE's servers.</p>
                <div className="rounded-lg border border-[#27272A] bg-[#09090B] p-3 space-y-1 max-h-36 overflow-y-auto">
                  {[...selectedMenuIds].map(id => (
                    <p key={id} className="text-xs font-mono text-[#A1A1AA]">{id}</p>
                  ))}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="bg-[#27272A] border-[#3F3F46] hover:bg-[#3F3F46]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setLineDeleteConfirm(false); setLineDeleteConfirm2(true); }} className="bg-red-600 hover:bg-red-700">
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* LINE menus delete — step 2 final confirm */}
      <AlertDialog open={lineDeleteConfirm2} onOpenChange={open => { if (!lineDeleting) setLineDeleteConfirm2(open); }}>
        <AlertDialogContent className="bg-[#18181B] border-red-500/30 max-w-[90vw] sm:max-w-lg">
          {lineDeleting ? (
            <div className="flex flex-col items-center gap-4 py-6">
              <RefreshCw className="w-8 h-8 text-red-400 animate-spin" />
              <div className="text-center">
                <p className="font-semibold text-white">Removing menus from LINE...</p>
                <p className="text-sm text-[#71717A] mt-1">
                  Deleting {selectedMenuIds.size} menu{selectedMenuIds.size !== 1 ? 's' : ''} via LINE API. Please wait.
                </p>
              </div>
            </div>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-red-400">Permanently delete from LINE?</AlertDialogTitle>
                <AlertDialogDescription className="text-[#A1A1AA]">
                  These <span className="text-white font-medium">{selectedMenuIds.size} menu{selectedMenuIds.size !== 1 ? 's' : ''}</span> will be removed from LINE's API permanently. Any LINE users or aliases referencing them will stop working. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                <AlertDialogCancel className="bg-[#27272A] border-[#3F3F46] hover:bg-[#3F3F46]">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 hover:bg-red-700"
                  onClick={async () => {
                    setLineDeleting(true);
                    try {
                      const r = await deleteLineMenus(channelId, [...selectedMenuIds]);
                      const deleted = r.results.filter(x => x.status === 'deleted').length;
                      const skipped = r.results.filter(x => x.status === 'skipped').length;
                      const errors = r.results.filter(x => x.status === 'error').length;
                      if (deleted > 0) toast.success(`${deleted} menu${deleted !== 1 ? 's' : ''} deleted from LINE`);
                      if (skipped > 0) toast.warning(`${skipped} menu${skipped !== 1 ? 's' : ''} skipped (live on LINE: default menu or alias target)`);
                      if (errors > 0) toast.error(`${errors} menu${errors !== 1 ? 's' : ''} failed to delete`);
                      setSelectedMenuIds(new Set());
                      setLineDeleteConfirm2(false);
                      await loadLineData();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to delete menus');
                      setLineDeleteConfirm2(false);
                    } finally { setLineDeleting(false); }
                  }}
                >
                  Delete from LINE permanently
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>

      {/* QR dialog */}
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-[90vw] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add on LINE</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Scan to add <span className="text-white font-medium">{botInfo?.displayName}</span> as a LINE friend</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="p-4 bg-white rounded-xl">
              <QRCodeSVG value={`https://line.me/R/ti/p/${botInfo?.basicId}`} size={200} level="M" />
            </div>
            <div className="text-center">
              <p className="text-sm font-mono text-[#A1A1AA]">{botInfo?.basicId}</p>
              <a href={`https://line.me/R/ti/p/${botInfo?.basicId}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-xs text-[#06C755] hover:underline">
                Open in LINE <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default ChannelDetail;
